import { HttpError } from "@microbeta/common-frontend";
import {
  AreaSchema,
  AreasArraySchema,
  CreateAreaSchema,
} from "@microbeta/common-types";
import type * as z from "zod";
import config from "../../../config";
import { httpClient } from "../../../shared/utils/httpClientInstance";

type CreateAreaPayload = z.infer<typeof CreateAreaSchema>;
type Area = z.infer<typeof AreaSchema>;

interface PaginatedAreasResponseRaw {
  areas: unknown[];
  total: number;
  limit: number;
  offset: number;
}

// Add pagination response type
export interface PaginatedAreasResponse {
  areas: Area[];
  total: number;
  limit: number;
  offset: number;
}

const handleZodError = (error: z.ZodError, context: string): Error => {
  console.error(`${context} validation failed:`, error.issues);
  const errorMessages = error.issues
    .map((e) => `${e.path.join(".")} - ${e.message} (${e.code})`)
    .join(", ");
  return new Error(`Invalid data (${context}): ${errorMessages}`);
};

const BASE_URL = `${config.VITE_CONTENT_SERVICE_URL}/content`;

/**
 * Initiates the creation of a new draft area.
 * @param teamId - Optional team ID to assign the area to
 * @returns Promise resolving to the ID of the newly created draft area.
 */
export const createDraftArea = async (
  teamId?: string
): Promise<{ id: string; slug: string }> => {
  try {
    const result = await httpClient.post<{ id: string; slug: string }>(
      `${BASE_URL}/areas/draft`,
      teamId ? { team_id: teamId } : {}
    );

    return result;
  } catch (error: unknown) {
    console.error("Error creating draft area:", error);
    if (error instanceof HttpError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new Error(`Failed to create draft area: ${error.message}`);
    }
    throw new Error("An unknown error occurred while creating the draft area.");
  }
};

export const createArea = async (
  areaData: CreateAreaPayload
): Promise<area> => {
  const validatedInput = CreateAreaSchema.safeParse(areaData);
  if (!validatedInput.success) {
    throw handleZodError(validatedInput.error, "create area input");
  }

  try {
    const result = await httpClient.post<unknown>(
      `${BASE_URL}/areas`,
      validatedInput.data
    );
    const parsedResult = AreaSchema.safeParse(result);
    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "create area response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error("Error creating area:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to create area: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while creating the area.");
  }
};

// Helper function to transform raw area data to match schema
const transformAreaData = (item: Record<string, unknown>) => {
  // Generate slug from name if missing
  // Check for slug in multiple ways to handle null/undefined/empty string
  const rawSlug = item.slug;
  const hasSlugFromAPI =
    rawSlug !== null && rawSlug !== undefined && rawSlug !== "";

  const generatedSlug = hasSlugFromAPI
    ? String(rawSlug) // Convert to string to ensure it's a string type
    : item.name
      ? (item.name as string)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
      : "";

  // Create a new object with default values
  return {
    id: (item.id as string) || "",
    name: (item.name as string) || "",
    slug: generatedSlug, // Use generated slug (from API or fallback)
    description: (item.description as string) || "",
    // Ensure these fields are properly formatted
    points_of_interest: Array.isArray(item.points_of_interest)
      ? item.points_of_interest
      : undefined,
    images: Array.isArray(item.images)
      ? item.images.map((img: unknown) => {
          if (typeof img === "string") {
            const uuid =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : "00000000-0000-0000-0000-000000000000";
            return {
              id: uuid,
              original: { url: img },
            };
          }
          if (typeof img === "object" && img !== null && !img.id) {
            const uuid =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : "00000000-0000-0000-0000-000000000000";
            return {
              ...img,
              id: uuid,
            };
          }
          return img;
        })
      : null,

    metadata: item.metadata === undefined ? null : item.metadata,
    // Handle area_map_data field - preserve GeoJSON FeatureCollection
    area_map_data: item.area_map_data || null,
    team_id:
      item.team_id === undefined ? null : (item.team_id as string | null),
    status: (item.status as string) || "draft",
    created_by: (item.created_by as string | null) || null,
    created_at: (item.created_at as string) || new Date().toISOString(),
    updated_at: (item.updated_at as string) || new Date().toISOString(),
    published_at: (item.published_at as string | undefined) || undefined,
  };
};

export const getAreas = async (
  publishedOnly = false,
  limit = 50,
  offset = 0,
  summary = false
): Promise<PaginatedAreasResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (publishedOnly) {
      queryParams.append("published", "true");
    }
    queryParams.append("limit", limit.toString());
    queryParams.append("offset", offset.toString());
    if (summary) {
      queryParams.append("summary", "true");
    }

    const url = `${BASE_URL}/areas?${queryParams.toString()}`;
    const result = await httpClient.get<PaginatedAreasResponseRaw | unknown[]>(
      url
    );

    // Handle legacy response format
    if (Array.isArray(result)) {
      const processedData = result.map(transformAreaData);

      const parsedResult = AreasArraySchema.safeParse(processedData);
      if (!parsedResult.success) {
        console.error(
          "[getAreas] Schema validation failed:",
          JSON.stringify(parsedResult.error, null, 2)
        );
        throw handleZodError(parsedResult.error, "fetch areas response");
      }

      return {
        areas: parsedResult.data,
        total: parsedResult.data.length,
        limit,
        offset,
      };
    }

    // Handle modern paginated response format
    const areasData = result.areas || [];

    const processedData = areasData.map(transformAreaData);

    const parsedResult = AreasArraySchema.safeParse(processedData);

    if (!parsedResult.success) {
      console.error(
        "[getAreas] Schema validation failed (paginated format):",
        JSON.stringify(parsedResult.error, null, 2)
      );
      throw handleZodError(parsedResult.error, "fetch areas response");
    }

    return {
      areas: parsedResult.data,
      total: result.total,
      limit: result.limit,
      offset: result.offset,
    };
  } catch (error: unknown) {
    console.error("Error fetching areas:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch areas: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while fetching areas.");
  }
};

/**
 * Fetches published areas within a geographic bounding box for map viewport optimization.
 * @param southWest - Southwest corner coordinates [longitude, latitude]
 * @param northEast - Northeast corner coordinates [longitude, latitude]
 * @param limit - Number of results per page
 * @param offset - Number of results to skip
 * @returns Promise resolving to paginated areas response
 */
export const getAreasInBounds = async (
  southWest: [number, number],
  northEast: [number, number],
  limit = 100,
  offset = 0
): Promise<PaginatedAreasResponse> => {
  try {
    const [swLng, swLat] = southWest;
    const [neLng, neLat] = northEast;

    const queryParams = new URLSearchParams();
    queryParams.append("swLng", swLng.toString());
    queryParams.append("swLat", swLat.toString());
    queryParams.append("neLng", neLng.toString());
    queryParams.append("neLat", neLat.toString());
    queryParams.append("limit", limit.toString());
    queryParams.append("offset", offset.toString());

    const url = `${BASE_URL}/areas?${queryParams.toString()}`;
    const result = await httpClient.get<PaginatedAreasResponseRaw>(url);

    const areasData = result.areas || [];
    const processedData = areasData.map(transformAreaData);

    const parsedResult = AreasArraySchema.safeParse(processedData);
    if (!parsedResult.success) {
      console.error(
        "Validation errors for bounds query:",
        parsedResult.error.issues
      );
      throw handleZodError(
        parsedResult.error,
        "fetch areas in bounds response"
      );
    }

    return {
      areas: parsedResult.data,
      total: result.total,
      limit: result.limit,
      offset: result.offset,
    };
  } catch (error: unknown) {
    console.error("Error fetching areas in bounds:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch areas in bounds: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while fetching areas in bounds."
    );
  }
};

export const getAreaById = async (id: string): Promise<Area | null> => {
  if (!id) throw new Error("area ID is required.");

  try {
    const result = await httpClient.get<unknown>(`${BASE_URL}/areas/${id}`);

    // Pre-process the data
    const processedData = transformAreaData(result as Record<string, unknown>);

    const parsedResult = AreaSchema.safeParse(processedData);
    if (!parsedResult.success) {
      console.error(
        "Problematic data snippet:",
        JSON.stringify(processedData).substring(0, 500)
      );
      console.error("Validation errors:", parsedResult.error.issues);

      // Try to force the data to match the schema as a last resort
      const forcedData = {
        ...processedData,
        created_by: processedData.created_by || "unknown",
        images: processedData.images || [],
      };

      const forcedResult = AreaSchema.safeParse(forcedData);
      if (forcedResult.success) {
        return forcedResult.data;
      }

      throw handleZodError(parsedResult.error, "fetch area by id response");
    }

    return parsedResult.data;
  } catch (error: unknown) {
    if (error instanceof HttpError && error.status === 404) {
      return null;
    }
    console.error(`Error fetching area ${id}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch area: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`An unknown error occurred while fetching area ${id}.`);
  }
};

export const getAreaBySlug = async (slug: string): Promise<Area> => {
  if (!slug) throw new Error("area Slug is required.");

  try {
    const result = await httpClient.get<unknown>(
      `${BASE_URL}/areas/slug/${slug}`
    );

    // Pre-process the data
    const processedData = transformAreaData(result as Record<string, unknown>);

    const parsedResult = AreaSchema.safeParse(processedData);
    if (!parsedResult.success) {
      console.error(
        "Problematic data snippet:",
        JSON.stringify(processedData).substring(0, 500)
      );
      console.error("Validation errors:", parsedResult.error.issues);

      // Try to force the data to match the schema as a last resort
      const forcedData = {
        ...processedData,
        created_by: processedData.created_by || "unknown",
        images: processedData.images || [],
      };

      const forcedResult = AreaSchema.safeParse(forcedData);
      if (forcedResult.success) {
        return forcedResult.data;
      }

      throw handleZodError(parsedResult.error, "fetch area by slug response");
    }

    return parsedResult.data;
  } catch (error: unknown) {
    console.error(`Error fetching area slug ${slug}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch area: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`An unknown error occurred while fetching area ${slug}.`);
  }
};

export const updateArea = async (
  id: string,
  areaData: Partial<area>
): Promise<area> => {
  if (!id) throw new Error("area ID is required for updating.");

  try {
    const result = await httpClient.put<unknown>(
      `${BASE_URL}/areas/${id}`,
      areaData
    );

    // Pre-process the data
    const processedData = transformAreaData(result as Record<string, unknown>);

    const parsedResult = AreaSchema.safeParse(processedData);
    if (!parsedResult.success) {
      console.error(
        "Problematic data snippet:",
        JSON.stringify(processedData).substring(0, 500)
      );
      console.error("Validation errors:", parsedResult.error.issues);

      // Try to force the data to match the schema as a last resort
      const forcedData = {
        ...processedData,
        created_by: processedData.created_by || "unknown",
        images: processedData.images || [],
      };

      const forcedResult = AreaSchema.safeParse(forcedData);
      if (forcedResult.success) {
        return forcedResult.data;
      }

      throw handleZodError(parsedResult.error, "update area response");
    }

    return parsedResult.data;
  } catch (error: unknown) {
    console.error(`Error updating area ${id}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to update area: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`An unknown error occurred while updating area ${id}.`);
  }
};

export const deleteArea = async (id: string): Promise<void> => {
  if (!id) throw new Error("area ID is required for deletion.");

  try {
    await httpClient.del(`${BASE_URL}/areas/${id}`);
  } catch (error: unknown) {
    console.error(`Error deleting area ${id}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to delete area: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`An unknown error occurred while deleting area ${id}.`);
  }
};

export const deleteAreaImage = async (
  areaId: string,
  imageUrl: string
): Promise<{ message: string; updatedImages: string[] }> => {
  try {
    const result = await httpClient.del<{
      message: string;
      updatedImages: string[];
    }>(`${BASE_URL}/areas/${areaId}/images`, {
      body: JSON.stringify({ imageUrl }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return result;
  } catch (error: unknown) {
    console.error(`Error deleting area image for ${areaId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to delete area image: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while deleting the area image.");
  }
};

export const addAreaImage = async (
  areaId: string,
  imageUrl: string
): Promise<{ message: string; updatedImages: string[] }> => {
  try {
    const result = await httpClient.post<{
      message: string;
      updatedImages: string[];
    }>(`${BASE_URL}/areas/${areaId}/images`, { imageUrl });
    return result;
  } catch (error: unknown) {
    console.error(`Error adding area image for ${areaId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to add area image: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while adding the area image.");
  }
};
